import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent } from "react";

import { YesOrNo } from "../../interfaces/enum";
import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import { Sanitizers } from "./sanitizeInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

export interface RadioOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

interface Props<T> {
  fieldData?: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  selectedRadioItem?: T;
  id?: string;
  sanitize?: Sanitizers<T>;
  validators?: Validators<T>;
  onBlur?: (value: T) => void;
  onChange?: (value: T) => void;
  propertyName?: string;
  required?: boolean;
  disabled?: boolean;
  radioRowClassName?: string;
  radioClassName?: string;
  wrapOptionInDiv?: YesOrNo;
  options: RadioOption<T>[];
  ariaLabelledBy?: string;
}

export function VerticalRadioInput<T extends string>({
  options: optionsProp,
  radioRowClassName: radioRowClassNameProp,
  radioClassName: radioClassNameProp,
  wrapOptionInDiv: wrapOptionInDivProp,
  fieldName: fieldNameProp,
  id: idProp,
  name,
  modelState,
  onChange: onChangeProp,
  setModelState,
  validators,
  propertyName,
  fieldData,
  disabled,
  selectedRadioItem,
  required,
  ariaLabelledBy,
}: Props<T>) {
  const options = optionsProp || [];

  const radioRowClassName = radioRowClassNameProp || "form-row";
  const radioClassName = radioClassNameProp || "form-item";
  const wrapOptionInDiv = wrapOptionInDivProp === "Yes";

  const { fieldName } = useFieldName(name, fieldNameProp);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as T;
    if (onChangeProp) {
      onChangeProp(value);
    }

    if (setModelState) {
      setModelState(unobtrusiveValidation(validators, modelState, propertyName, value));
    }
  };

  const onBlur = () => {
    if (setModelState) {
      setModelState(unobtrusiveValidation(validators, modelState, propertyName, selectedRadioItem ?? ("" as T)));
    }
  };

  const id = idProp || fieldName;
  const hasError = hasValidationMessage(modelState, propertyName);
  const errorId = `${id}-error`;

  return (
    <div
      role="radiogroup"
      aria-required={required}
      aria-invalid={hasError}
      aria-labelledby={ariaLabelledBy}
      onBlur={onBlur}
    >
      {hasError && (
        <div className="form-row">
          <ValidationMessage modelState={modelState} property={propertyName} id={errorId} />
        </div>
      )}
      {options.map((option, index) => {
        const radioBtnFieldName = `${id}${index}`;
        const tooltipFieldName = fieldData ? `Tooltip${radioBtnFieldName.replace(/\d+/g, "")}` : undefined;
        const radioBtnTooltip = fieldData && tooltipFieldName ? fieldData[tooltipFieldName]?.value : "";

        if (disabled) {
          option.disabled = true;
        }

        const optionElement = (
          <>
            <input
              id={radioBtnFieldName}
              name={fieldName}
              type="radio"
              value={option.value}
              required={required}
              disabled={option.disabled || false}
              checked={selectedRadioItem === option.value}
              onChange={onChange}
            />
            <label htmlFor={radioBtnFieldName} className="radio">
              {option.label}
              <Tooltip fieldName={radioBtnFieldName} value={radioBtnTooltip} />
            </label>
          </>
        );

        return (
          <div key={fieldName + option.value} className={radioRowClassName}>
            {wrapOptionInDiv ? <div className={radioClassName}>{optionElement}</div> : optionElement}
          </div>
        );
      })}
    </div>
  );
}
